import { FormattedMessage, useIntl } from 'react-intl';
import { Moment } from 'moment';
import {
  Card,
  Checkbox,
  Col,
  Form,
  FormInstance,
  Input,
  Row,
  Select,
  Typography,
} from 'antd';

import {
  useDefaultEmptyFieldError,
  useDefaultMaxLengthFieldError,
} from '@/hooks';

import {
  WorkOrderComplementStatus,
  WorkOrderComplementTankLevel,
  WorkOrderComplementType,
} from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import {
  ComplementOriginEnum,
  CustomerType,
  WorkOrder,
} from '@/core/domain/workorder/WorkOrders/WorkOrders.types';

import { flatInfiniteData } from '@/utils/request/flatInfiniteData';

import {
  CompaniesInfiniteSelectField,
  FloatingFormItem,
  Group,
  InputDate,
  InputDecimal,
  PhoneFormField,
} from '@/components/_common';
import { useWorkorderDetailContext } from '../../WorkorderDetailProvider';
import ComplementEstimatedLeaveDateField from './ComplementEstimatedLeaveDateField';
import { useCompaniesByLoggedEmployee } from '@/core/domain/enterprise/Companies/Companies.hooks';
import InsurerInfiniteSelectField from '@/components/_common/InsurerInfiniteSelectField';
import EmployeesInfiniteSelectField from '@/components/_common/EmployeesInfiniteSelectField';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { isAppPortocar } from '@/index';
import { useMemo } from 'react';
import ComplementScheduleDateField from './ComplementScheduleDateField';

const { Title } = Typography;

export interface WorkOrderComplementFormData {
  complementId?: number;
  companyId: number;
  estimatorId: number;
  type: WorkOrderComplementType;
  customerType: CustomerType;
  insurerId?: number;
  accidentNumber?: string;
  insurerDeductibleAmount?: number;
  entryDate?: Moment;
  authorizationDate?: Moment;
  closingDate?: Moment;
  estimatedLeaveDate?: Moment;
  returnDate?: Moment;
  prisma?: string;
  currentKilometers?: number;
  exitKilometers?: number;
  tankLevel?: WorkOrderComplementTankLevel;
  lastOilChange?: Moment;
  lastOilChangeKilometers?: number;
  thirdPartyCustomer?: boolean;
  indicatedBy?: string;
  contact?: string;
  origin?: string;
  scheduleDateTime?: Moment;
}

interface WorkOrderComplementFormProps {
  form: FormInstance<WorkOrderComplementFormData>;
  workOrder?: WorkOrder;
}

function WorkOrderComplementForm({
  form,
  workOrder,
}: WorkOrderComplementFormProps) {
  const intl = useIntl();
  const defaultEmptyFieldError = useDefaultEmptyFieldError();
  const companyIdField = Form.useWatch('companyId', form);

  const {
    workorder: { data: workorder },
    complement: { data: complement },
    company: { companyConfig },
  } = useWorkorderDetailContext();

  const isPortoSchedule =
    complement?.origin === ComplementOriginEnum.PortoSchedule ||
    complement?.origin === ComplementOriginEnum.PortoScheduleInternal;

  const defaultMaxLenghtFieldError = useDefaultMaxLengthFieldError();

  const { data: companiesData } = useCompaniesByLoggedEmployee();
  const companies = flatInfiniteData(companiesData);

  const disableCompany = workOrder?.complements?.some((complement) =>
    [
      WorkOrderComplementStatus.Authorized,
      WorkOrderComplementStatus.Closed,
    ].includes(complement?.status),
  );

  const isClosed = complement?.status === WorkOrderComplementStatus.Closed;
  const isOpenOrCancelled =
    complement?.status === WorkOrderComplementStatus.Open ||
    complement?.status === WorkOrderComplementStatus.Cancelled;
  const isOpen = complement?.status === WorkOrderComplementStatus.Open;
  const isAuthorized =
    complement?.status === WorkOrderComplementStatus.Authorized;

  const handleSelectCompany = (companyId: number) => {
    const company = companies.find((c) => c.id === companyId);

    if (!company || !companyId) {
      form.resetFields(['estimatorId']);
    }
  };

  const lastOilChangeDisable = useMemo(() => {
    if (
      complement?.status &&
      [
        WorkOrderComplementStatus.Closed,
        WorkOrderComplementStatus.Cancelled,
      ].includes(complement?.status)
    )
      return true;

    if (!workOrder?.vehicle?.id) return true;

    if (workOrder.vehicle?.fuelName?.toLowerCase()?.includes('elétrico'))
      return false;

    return !!complement?.lastOilChange && !!complement.lastOilChangeKilometers;
  }, [complement, workOrder]);

  return (
    <Form form={form} name="generalInfo">
      <Form.Item hidden name="complementId">
        <Input />
      </Form.Item>

      <Row gutter={[16, 8]}>
        <Col xs={24} sm={24} md={5}>
          <FloatingFormItem
            name="companyId"
            rules={[defaultEmptyFieldError]}
            label={intl.formatMessage({
              id: 'workOrders.domain.company',
            })}
            getValueFromEvent={(value) => {
              handleSelectCompany(value);
              return value;
            }}
          >
            <CompaniesInfiniteSelectField
              disabled={disableCompany || isClosed}
            />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={24} md={5}>
          <FloatingFormItem
            name="estimatorId"
            rules={[defaultEmptyFieldError]}
            label={intl.formatMessage({
              id: 'workOrders.domain.complements.estimator',
            })}
          >
            <EmployeesInfiniteSelectField
              queryFilter={{ estimator: true, companyId: companyIdField }}
              disabled={
                !hasPermissions([
                  Authorities.MOVIMENTATION_WORKORDER_SELECT_ANOTHER_EMPLOYEE,
                ]) || isClosed
              }
            />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={24} md={5}>
          <FloatingFormItem
            name="type"
            rules={[defaultEmptyFieldError]}
            label={intl.formatMessage({
              id: 'workOrders.domain.complements.type',
            })}
            initialValue={WorkOrderComplementType.WorkOrder}
          >
            <Select
              disabled={
                !hasPermissions([
                  Authorities.MOVIMENTATION_WORKORDER_CHANGE_TYPE,
                ]) ||
                !isOpen ||
                isClosed ||
                workorder?.isPortoBatteryWorkOrder
              }
              allowClear
              style={{ width: '100%' }}
              options={Object.values(WorkOrderComplementType).map((type) => ({
                label: intl.formatMessage({
                  id: `workOrders.${
                    isAppPortocar && type === WorkOrderComplementType.Courtesy
                      ? 'domain.enums.work_order_type.insideCourtesy'
                      : type
                  }`,
                }),
                value: type,
                disabled:
                  isAppPortocar &&
                  (type === WorkOrderComplementType.Revision ||
                    type === WorkOrderComplementType.TotalLoss),
              }))}
            />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={24} md={4}>
          <FloatingFormItem
            name="customerType"
            rules={[defaultEmptyFieldError]}
            label={intl.formatMessage({
              id: 'workOrders.domain.complements.customerType',
            })}
            getValueFromEvent={(type) => {
              form.resetFields([
                'insurerId',
                'accidentNumber',
                'insurerDeductibleAmount',
              ]);
              return type;
            }}
          >
            <Select
              disabled={!isOpenOrCancelled || isClosed}
              allowClear
              style={{ width: '100%' }}
              options={Object.values(CustomerType).map((a) => ({
                label: intl.formatMessage({ id: `workOrders.${a}` }),
                value: a,
              }))}
            />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={24} md={5}>
          <FloatingFormItem name="origin" label="Origem">
            <Input disabled />
          </FloatingFormItem>
        </Col>

        <Col span={24}>
          <Form.Item
            shouldUpdate={(prev, curr) =>
              prev.customerType !== curr.customerType
            }
            noStyle
          >
            {({ getFieldValue }) => {
              const customerType = getFieldValue('customerType');

              return (
                customerType === CustomerType.Insurance && (
                  <Row gutter={[16, 8]}>
                    <Col span={24}>
                      <Title level={5}>
                        <FormattedMessage id="workOrders.domain.complements.insurer" />
                      </Title>
                    </Col>

                    <Col span={24} md={12} lg={6}>
                      <FloatingFormItem
                        name="insurerId"
                        label={intl.formatMessage({
                          id: 'workOrders.domain.complements.insurer',
                        })}
                        rules={[defaultEmptyFieldError]}
                      >
                        <InsurerInfiniteSelectField
                          disabled={isClosed}
                          showCnpj
                        />
                      </FloatingFormItem>
                    </Col>

                    <Col span={24} md={12} lg={6}>
                      <FloatingFormItem
                        name="accidentNumber"
                        label={intl.formatMessage({
                          id: 'workOrders.domain.complements.accidentNumber',
                        })}
                      >
                        <Input disabled={isClosed} />
                      </FloatingFormItem>
                    </Col>

                    <Col span={24} md={12} lg={6}>
                      <FloatingFormItem
                        name="insurerDeductibleAmount"
                        initialValue={0}
                        label={intl.formatMessage({
                          id: 'workOrders.domain.complements.insurerDeductibleAmount',
                        })}
                      >
                        <InputDecimal
                          disabled={
                            !hasPermissions([
                              Authorities.MOVIMENTATION_WORKORDER_CLOSED_BLOCK_EDIT_FRANCHISES,
                            ]) || isClosed
                          }
                        />
                      </FloatingFormItem>
                    </Col>

                    <Col span={24} md={12} lg={6}>
                      <Form.Item
                        name="thirdPartyCustomer"
                        valuePropName="checked"
                      >
                        <Checkbox>Cliente de terceiros</Checkbox>
                      </Form.Item>
                    </Col>
                  </Row>
                )
              );
            }}
          </Form.Item>
        </Col>

        <Col span={24} style={{ width: '100%' }}>
          <Row gutter={[16, 8]}>
            <Col xs={24} sm={24} md={8} lg={4}>
              <FloatingFormItem
                name="entryDate"
                rules={[defaultEmptyFieldError]}
                label={intl.formatMessage({
                  id: 'workOrders.domain.complements.entryDate',
                })}
              >
                <InputDate
                  showTime
                  disabled={
                    !hasPermissions([
                      Authorities.MOVIMENTATION_WORKORDER_EDIT_ENTRY_DATE,
                    ]) || isClosed
                  }
                />
              </FloatingFormItem>
            </Col>

            <Col xs={24} sm={24} md={8} lg={5}>
              <FloatingFormItem
                name="authorizationDate"
                label={intl.formatMessage({
                  id: 'workOrders.domain.complements.authorizationDate',
                })}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value: Moment) {
                      if (
                        !value ||
                        value.isSameOrAfter(
                          getFieldValue('entryDate').milliseconds(0),
                        )
                      ) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error('Deve ser maior que a data de entrada'),
                      );
                    },
                  }),
                ]}
              >
                <InputDate disabled={!isAuthorized || isClosed} showTime />
              </FloatingFormItem>
            </Col>

            <Col xs={24} sm={24} md={8} lg={5}>
              <FloatingFormItem
                name="closingDate"
                label={intl.formatMessage({
                  id: 'workOrders.domain.complements.closingDate',
                })}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value: Moment) {
                      if (
                        !value ||
                        value.isSameOrAfter(
                          getFieldValue('authorizationDate').milliseconds(0),
                        )
                      ) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error('Deve ser maior que a data de autorização'),
                      );
                    },
                  }),
                ]}
              >
                <InputDate
                  disabled={
                    isAppPortocar ||
                    !hasPermissions([
                      Authorities.MOVIMENTATION_WORKORDER_AUTHORIZED_EDIT_CLOSING_DATE,
                    ]) ||
                    !isClosed
                  }
                  showTime
                />
              </FloatingFormItem>
            </Col>

            <Col xs={24} sm={24} md={12} lg={6}>
              <ComplementEstimatedLeaveDateField
                disabled={!isClosed && !isAuthorized && !isOpen}
              />
            </Col>

            <Col xs={24} sm={24} md={12} lg={4}>
              <FloatingFormItem
                name="returnDate"
                label={intl.formatMessage({
                  id: 'workOrders.domain.complements.returnDate',
                })}
              >
                <InputDate disabled={!isClosed && !isAuthorized} showTime />
              </FloatingFormItem>
            </Col>
          </Row>
        </Col>

        {isPortoSchedule && (
          <Col xs={24} sm={24} md={12} lg={6}>
            <ComplementScheduleDateField />
          </Col>
        )}

        <Col xs={24} sm={24} md={!!complement?.ssLocaliza ? 6 : 8}>
          <FloatingFormItem
            name="prisma"
            label={intl.formatMessage({
              id: 'workOrders.domain.complements.prisma',
            })}
            rules={[
              {
                required: companyConfig?.prisma,
                message: intl.formatMessage({
                  id: 'workOrders.fieldRequiredByConfigCompany',
                }),
              },
            ]}
          >
            <Input disabled={isClosed} />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={24} md={!!complement?.ssLocaliza ? 6 : 8}>
          <FloatingFormItem
            name="currentKilometers"
            label={intl.formatMessage({
              id: 'workOrders.domain.complements.currentKilometers',
            })}
            rules={[
              {
                required: companyConfig?.isKmRequired,
                message: intl.formatMessage({
                  id: 'workOrders.fieldRequiredByConfigCompany',
                }),
              },
            ]}
          >
            <InputDecimal
              min={0}
              max={5000000}
              fractions={0}
              disabled={isClosed}
            />
          </FloatingFormItem>
        </Col>

        {isClosed && (
          <Col xs={24} sm={24} md={!!complement?.ssLocaliza ? 6 : 8}>
            <FloatingFormItem
              name="exitKilometers"
              label={intl.formatMessage({
                id: 'workOrders.domain.complements.exitKilometers',
              })}
              rules={[
                {
                  required: companyConfig?.isKmRequired,
                  message: intl.formatMessage({
                    id: 'workOrders.fieldRequiredByConfigCompany',
                  }),
                },
              ]}
            >
              <InputDecimal min={0} fractions={0} />
            </FloatingFormItem>
          </Col>
        )}

        <Col xs={24} sm={24} md={!!complement?.ssLocaliza ? 6 : 8}>
          <FloatingFormItem
            name="tankLevel"
            label={intl.formatMessage({
              id: 'workOrders.domain.complements.tankLevel',
            })}
            rules={[
              {
                required: companyConfig?.tankLevel,
                message: intl.formatMessage({
                  id: 'workOrders.fieldRequiredByConfigCompany',
                }),
              },
            ]}
          >
            <Select
              allowClear
              style={{ width: '100%' }}
              options={Object.values(WorkOrderComplementTankLevel).map((a) => ({
                label: intl.formatMessage({ id: `workOrders.${a}` }),
                value: a,
              }))}
              disabled={isClosed}
            />
          </FloatingFormItem>
        </Col>

        {!!complement?.ssLocaliza && (
          <Col span={24} md={!!complement?.ssLocaliza ? 6 : 8}>
            <FloatingFormItem name="ssLocaliza" label="SS Localiza">
              <Input disabled />
            </FloatingFormItem>
          </Col>
        )}

        {!!companyConfig?.seeIndicatedBy && (
          <>
            <Col span={24} md={!!complement?.ssLocaliza ? 6 : 8}>
              <FloatingFormItem
                name="indicatedBy"
                label="Corretor"
                rules={[defaultMaxLenghtFieldError(100)]}
              >
                <Input
                  disabled={
                    !hasPermissions([
                      Authorities.MOVIMENTATION_WORKORDER_AUTHORIZED_EDIT_INDICATE_PHONE,
                    ])
                  }
                />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={!!complement?.ssLocaliza ? 6 : 8}>
              <PhoneFormField
                fieldName="contact"
                label="Celular"
                required={false}
                disabled={
                  !hasPermissions([
                    Authorities.MOVIMENTATION_WORKORDER_AUTHORIZED_EDIT_INDICATE_PHONE,
                  ])
                }
              />
            </Col>
          </>
        )}

        {isAppPortocar && (
          <>
            <Col span={24} md={!!complement?.ssLocaliza ? 6 : 8}>
              <FloatingFormItem
                name="lastOilChange"
                label="Última troca de óleo"
              >
                <InputDate disabled={lastOilChangeDisable} />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={!!complement?.ssLocaliza ? 6 : 8}>
              <FloatingFormItem
                name="lastOilChangeKilometers"
                label="Última troca de óleo (KM)"
              >
                <InputDecimal fractions={0} disabled={lastOilChangeDisable} />
              </FloatingFormItem>
            </Col>

            {complement?.winchCode && (
              <Col span={24} md={!!complement?.ssLocaliza ? 6 : 8}>
                <FloatingFormItem name="winchCode" label="Código do guincho">
                  <InputDecimal disabled />
                </FloatingFormItem>
              </Col>
            )}

            {(!!workorder?.portoAssistantCode ||
              !!workorder?.portoAssistantVehicleCode) && (
              <Col span={24} md={!!complement?.ssLocaliza ? 6 : 8}>
                <Card
                  bodyStyle={{
                    padding: 10,
                    height: 48,
                    fontSize: '0.8rem',
                    display: 'flex',
                    lineHeight: 1.2,
                  }}
                  style={{ borderRadius: 8 }}
                >
                  <Group gap={10} style={{ marginRight: 24 }}>
                    <strong>Cód. Atendente:</strong>
                    <span>{workorder?.portoAssistantCode || '-'}</span>
                  </Group>
                  <Group gap={10}>
                    <strong>Cód. Viatura:</strong>
                    <span>{workorder?.portoAssistantVehicleCode || '-'}</span>
                  </Group>
                </Card>
              </Col>
            )}
          </>
        )}
      </Row>
    </Form>
  );
}

export default WorkOrderComplementForm;
